import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Form, Spinner } from "react-bootstrap";
import { Context } from "../App";
import AdminLayout from "../components/AdminLayout";
import AdminDateInput from "../components/AdminDateInput";
import * as Constants from "../api/constants";
import { normalizeTheatersList } from "../utils/normalizeTheaters";
import { enrichSlotFromTheatre, resolveMaxGuests } from "../utils/slotGuests";
import { toDateInputValue } from "../utils/eventDate";
import { persistAddonMeta, saveBookingFlow } from "../utils/bookingFlowStorage";
import { normalizeAddonMetaPayload } from "../utils/normalizeAddonMeta";
import { mergeSlotWithOffer } from "../utils/slotTimeOffers";
import { isSlotDisabled } from "../utils/slotAvailability";
import "../adminConsoleTheme.css";

const ADVANCE_INCL_FEE = 700;

export default function AdminPartyBossSlotBooking() {
  const navigate = useNavigate();
  const { context, setAddonMetaData, setTotalCost } = useContext(Context);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [theatres, setTheatres] = useState([]);
  const [addonMeta, setAddonMeta] = useState(null);
  const [eventDate, setEventDate] = useState(() => toDateInputValue(new Date()));
  const [theatreId, setTheatreId] = useState("");
  const [slotOptions, setSlotOptions] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingName, setBookingName] = useState("");
  const [persons, setPersons] = useState(2);
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [eventId, setEventId] = useState("");
  const [lineTotal, setLineTotal] = useState(0);

  useEffect(() => {
    if (!context?.isAdminLoggedIn) {
      navigate("/admin", { replace: true });
    }
  }, [context?.isAdminLoggedIn, navigate]);

  const loadTheatres = (dateStr) => {
    setLoading(true);
    setLoadError(null);
    axios
      .all([
        axios.post(Constants.GET_THEATERS_LIST, { date: dateStr }),
        axios.get(Constants.GET_META_DATA_ADDONS),
      ])
      .then(
        axios.spread((theatreRes, metaRes) => {
          const list = normalizeTheatersList(theatreRes?.data);
          const meta = normalizeAddonMetaPayload(metaRes?.data ?? {});
          setTheatres(list);
          setAddonMeta(meta);
          setAddonMetaData(meta);
          persistAddonMeta(meta);
          setLoading(false);
        })
      )
      .catch((err) => {
        setLoadError(err?.message || "Could not load theatres.");
        setLoading(false);
      });
  };

  useEffect(() => {
    loadTheatres(eventDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!theatreId) {
      setSlotOptions([]);
      setSelectedSlot(null);
      return;
    }
    const theatre = theatres.find((t) => String(t.id) === String(theatreId));
    const slots = Array.isArray(theatre?.timeslots) ? theatre.timeslots : [];
    const offers = addonMeta?.slotOffers ?? [];
    const enriched = slots.map((s) =>
      mergeSlotWithOffer(enrichSlotFromTheatre(s, theatre), theatre.id, eventDate, offers)
    );
    setSlotOptions(enriched);
    const firstOpen = enriched.find((s) => !isSlotDisabled(s, eventDate));
    setSelectedSlot(firstOpen || null);
  }, [theatreId, theatres, eventDate, addonMeta]);

  const effectiveMaxGuests = useMemo(
    () => resolveMaxGuests(selectedSlot, theatres.find((t) => String(t.id) === String(theatreId))),
    [selectedSlot, theatreId, theatres]
  );

  useEffect(() => {
    if (!selectedSlot) {
      setLineTotal(0);
      setTotalCost(0);
      return;
    }
    const n = Math.min(Math.max(1, Number(persons) || 1), effectiveMaxGuests);
    const cap = Number(selectedSlot.defaultCapacity) || 0;
    const base = Number(selectedSlot.basePrice) || 0;
    const extraRate = Number(selectedSlot.extraPerPerson) || 0;
    if (n > cap) {
      setLineTotal(base + (n - cap) * extraRate);
    } else {
      setLineTotal(base);
    }
    setTotalCost(
      n > cap
        ? base + (n - cap) * extraRate
        : base
    );
  }, [selectedSlot, persons, effectiveMaxGuests, setTotalCost]);

  const onDateChange = (e) => {
    const v = e.target.value;
    setEventDate(v);
    setTheatreId("");
    setSelectedSlot(null);
    loadTheatres(v);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!theatreId || !selectedSlot?.id || !eventId) return;
    if (isSlotDisabled(selectedSlot, eventDate)) return;
    const theatre = theatres.find((t) => String(t.id) === String(theatreId));
    try {
      sessionStorage.setItem("pb_review_back_path", "/admin/partyboss-slot-booking/addons");
      sessionStorage.setItem("pb_booking_traffic_source", "admin_slot_booking");
    } catch {
      /* ignore */
    }
    saveBookingFlow({
      adminBooking: true,
      slotId: Number(selectedSlot.id),
      eventId: Number(eventId),
      personsCount: Number(persons),
      bookingName: bookingName.trim(),
      whatsapp: whatsapp.trim(),
      email: email.trim(),
      eventDate,
      theatreBaseCost: lineTotal,
      selectedTheaterSnapshot: {
        id: theatre?.id,
        name: theatre?.name || theatre?.theatreName || "",
      },
      selectedTimeSnapshot: {
        id: selectedSlot.id,
        defaultCapacity: selectedSlot.defaultCapacity,
        extraPerPerson: selectedSlot.extraPerPerson,
        basePrice: selectedSlot.basePrice,
        slotTime: selectedSlot.slotTime,
      },
      slotOffer: selectedSlot?.slotOfferActive
        ? {
            active: true,
            headline: selectedSlot.slotOfferHeadline || "",
            offerBasePrice: Number(selectedSlot.basePrice ?? 0),
            regularSlotBasePrice: Number(selectedSlot.regularSlotBasePrice ?? 0),
            offerPriceKind: selectedSlot.slotOfferPriceKind || "RS",
            offerRawValue: Number(selectedSlot.slotOfferRawValue ?? 0),
            slotOfferDisplayLabel: selectedSlot.slotOfferDisplayLabel || "",
            id: selectedSlot.slotTimeOfferId ?? null,
          }
        : null,
      selectedAddons: {},
      nameOnCake: "",
      reviewTotalCost: lineTotal,
      wantsDecoration: "No",
    });
    navigate("/admin/partyboss-slot-booking/addons");
  };

  const canProceed =
    theatreId &&
    selectedSlot?.id &&
    !isSlotDisabled(selectedSlot, eventDate) &&
    bookingName.trim().length > 0 &&
    /^\d{10}$/.test(whatsapp.trim()) &&
    /^\S+@\S+\.\S+$/.test(email.trim()) &&
    Number(persons) >= 1 &&
    eventId !== "";

  const personOptions = useMemo(
    () => Array.from({ length: effectiveMaxGuests || 1 }, (_, i) => i + 1),
    [effectiveMaxGuests]
  );

  const balanceAtVenue = Math.max(0, lineTotal - ADVANCE_INCL_FEE);

  return (
    <AdminLayout title="PartyBoss slot booking">
      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" variant="light" />
        </div>
      ) : null}
      <div className="admin-console-card p-3 mb-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div>
            <div className="admin-console-card-title mb-1">PartyBoss slot booking</div>
            <p className="small text-secondary mb-0">
              Quick-book a slot for a customer: choose add-ons, then <strong>Pay Now</strong> (Razorpay) or{" "}
              <strong>Save and Pay Later</strong> (holds the slot; pending-payment emails — not listed under Payment failed).
            </p>
          </div>
          <button
            type="button"
            className="admin-console-nav-btn"
            onClick={() => navigate("/admin/dashboard")}
          >
            Back to bookings
          </button>
        </div>
        {loadError ? <p className="text-warning small">{loadError}</p> : null}
        <Form onSubmit={onSubmit} className="admin-console-filters flex-column align-items-stretch">
          <div className="row g-3">
            <div className="col-md-4">
              <Form.Label>Event date</Form.Label>
              <AdminDateInput value={eventDate} onChange={onDateChange} required />
            </div>
            <div className="col-md-4">
              <Form.Label>Theatre</Form.Label>
              <Form.Select
                value={theatreId}
                onChange={(e) => setTheatreId(e.target.value)}
                required
                className="form-select"
              >
                <option value="">Select theatre</option>
                {theatres.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name || t.theatreName}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-4">
              <Form.Label>Time slot</Form.Label>
              <Form.Select
                value={selectedSlot?.id ?? ""}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  setSelectedSlot(slotOptions.find((s) => Number(s.id) === id) || null);
                }}
                required
                disabled={!slotOptions.length}
                className="form-select"
              >
                {!slotOptions.length ? (
                  <option value="">No slots</option>
                ) : (
                  slotOptions.map((s) => (
                    <option key={s.id} value={s.id} disabled={isSlotDisabled(s, eventDate)}>
                      {s.slotTime}
                      {isSlotDisabled(s, eventDate) ? " (unavailable)" : ""}
                    </option>
                  ))
                )}
              </Form.Select>
            </div>
            <div className="col-md-6">
              <Form.Label>Customer name</Form.Label>
              <Form.Control
                value={bookingName}
                onChange={(e) => setBookingName(e.target.value)}
                required
                maxLength={40}
              />
            </div>
            <div className="col-md-3">
              <Form.Label>WhatsApp (10 digits)</Form.Label>
              <Form.Control
                type="tel"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value.replace(/\D/g, "").slice(0, 10))}
                required
              />
            </div>
            <div className="col-md-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="col-md-3">
              <Form.Label>Guests</Form.Label>
              <Form.Select
                value={persons}
                onChange={(e) => setPersons(Number(e.target.value))}
                className="form-select"
              >
                {personOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-3">
              <Form.Label>Event type</Form.Label>
              <Form.Select
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
                required
                className="form-select"
              >
                <option value="">Select</option>
                {addonMeta?.eventTypes?.map((ev) => (
                  <option key={ev.id} value={ev.id} disabled={ev.isActive !== "Y"}>
                    {ev.name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-6">
              <Form.Label>Package total (estimate)</Form.Label>
              <div className="text-white fw-bold">
                ₹ {lineTotal} · Advance ₹ {ADVANCE_INCL_FEE} · Balance at venue ₹ {balanceAtVenue}
              </div>
            </div>
          </div>
          <div className="mt-3">
            <button
              type="submit"
              className="admin-console-nav-btn admin-console-nav-btn--primary"
              disabled={!canProceed || loading}
            >
              Continue to add-ons
            </button>
          </div>
        </Form>
      </div>
    </AdminLayout>
  );
}
